import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/auth-context';
import { getWebsitePages, saveWebsitePage, WebsitePage } from '../../api/cms.api';
import { useVvvebBridge } from '../../hooks/useVvvebBridge';
import { getEnvironmentVariable } from '../../utils';

const platformUrl = getEnvironmentVariable('REACT_APP_PLATFORM_URL');

export default function WebsiteEditor() {
    const { id } = useParams<{ id: string }>();
    const websiteId = id ?? '';

    const { tokens } = useAuth();

    const [ pages, setPages ] = useState<WebsitePage[]>([]);
    const [ loading, setLoading ] = useState(true);

    const bridgeReadyRef = useRef(false);
    const pagesRef = useRef<WebsitePage[]>([]);
    const currentSubpageIdRef = useRef<number | null>(null);
    const blobUrlsRef = useRef<string[]>([]);

    useEffect(() => {
        pagesRef.current = pages;
    }, [ pages ]);

    useEffect(() => {
        return () => {
            blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    const buildPagesWithBlobUrls = useCallback(async (fetchedPages: WebsitePage[]): Promise<WebsitePage[]> => {
        const results = await Promise.allSettled(
            fetchedPages.map(async (page) => {
                const response = await fetch(page.url);
                const html = await response.text();
                const withBase = html.replace('<head>', `<head><base href="${page.url}">`);
                const blob = new Blob([ withBase ], { type: 'text/html' });
                const blobUrl = URL.createObjectURL(blob);
                blobUrlsRef.current.push(blobUrl);
                return { ...page, url: blobUrl };
            }),
        );

        return results.map((result, i) =>
            result.status === 'fulfilled' ? result.value : fetchedPages[i],
        );
    }, []);

    const handleSave = useCallback(
        async (getHtml: () => Promise<string>) => {
            const subpageId = currentSubpageIdRef.current;
            if (subpageId === null) {
                return;
            }
            try {
                const html = await getHtml();

                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                // @ts-ignore
                const css = [ ...doc.querySelectorAll('style') ]
                .map((s) => s.textContent ?? '')
                .join(' ');

                // @ts-ignore
                const js = [ ...doc.querySelectorAll('script:not([src])') ]
                .map((s) => s.textContent ?? '')
                .join(' ');

                const bodyHtml = doc.body.innerHTML;

                const currentPage = pages.find(page => page.subpageId === subpageId);
                if (!currentPage) {
                    return;
                }

                await saveWebsitePage(websiteId, currentPage.pageUrl, { html: bodyHtml, css, js });
                window.location.href = `${platformUrl}/admin/dang/cms`;
            } catch (err) {
                console.error('Save failed:', err);
            }
        },
        [ pages, websiteId ],
    );

    const { iframeRef, getHtml, setEditorPages } = useVvvebBridge({
        token: tokens.reactToken,
        onReady: () => {
            bridgeReadyRef.current = true;
            if (pagesRef.current.length > 0) {
                currentSubpageIdRef.current = pagesRef.current[0].subpageId;
                setEditorPages(pagesRef.current, pagesRef.current[0].url);
            }
        },
        onSaveTriggered: () => handleSave(getHtml),
        onPageLoaded: (payload) => {
            if (payload.subpageId !== undefined) {
                currentSubpageIdRef.current = payload.subpageId;
            }
        },
    });

    useEffect(() => {
        if (!websiteId) {
            return;
        }

        setLoading(true);

        getWebsitePages(websiteId)
        .then((fetchedPages) => buildPagesWithBlobUrls(fetchedPages))
        .then((pagesWithBlobs) => {
            setPages(pagesWithBlobs);

            if (bridgeReadyRef.current && pagesWithBlobs.length > 0) {
                currentSubpageIdRef.current = pagesWithBlobs[0].subpageId;
                setEditorPages(pagesWithBlobs, pagesWithBlobs[0].url);
            }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }, [ websiteId, tokens.reactToken, setEditorPages, buildPagesWithBlobUrls ]);

    if (loading) {
        return (
            <div style={styles.loadingScreen}>
                <div style={styles.loadingSpinner}/>
                <p style={styles.loadingText}>Loading editor...</p>
            </div>
        );
    }

    if (pages.length === 0) {
        return (
            <div style={styles.loadingScreen}>
                <p style={styles.loadingText}>No pages found for this website.</p>
            </div>
        );
    }

    return (
        <iframe
            ref={iframeRef}
            src="/vvvebjs/editor.html"
            style={styles.iframe}
            title="Page Editor"
        />
    );
}

const styles: Record<string, React.CSSProperties> = {
    iframe: {
        display: 'block',
        width: '100%',
        height: 'calc(100vh - 52px)',
        border: 'none',
    },
    loadingScreen: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 52px)',
        background: '#0f1117',
        gap: 16,
    },
    loadingSpinner: {
        width: 36,
        height: 36,
        border: '3px solid #2a2d3a',
        borderTop: '3px solid #3b82f6',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    loadingText: {
        color: '#6b7280',
        fontSize: 14,
        margin: 0,
    },
};
